import React, {useEffect, useState} from 'react';

import {GetAnalysisScratchFile} from 'analysis-tool/AnalysisTypes';
import {MaybeRenderWithSignedURL} from 'external-files/SignedExtFileURLProxy';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as net from 'util/Net';

interface ExternalFileContentLoaderProps {
  signedURL: string;
  fileType: string;
  children: (content: string) => JSX.Element;
}

function ExternalFileContentLoader({
  signedURL,
  fileType,
  children,
}: ExternalFileContentLoaderProps): JSX.Element {
  const [content, setContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    net
      .getPromise(signedURL)
      .then(setContent)
      .catch(() => {
        setError(
          `Failed to load ${fileType} file. Please contact Benchling support if the problem persists.`
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [signedURL, fileType]);

  if (isLoading) {
    return <CenteredHorizontalThrobber />;
  }

  if (error) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={error} />;
  }

  return children(content || '');
}

interface ExternalFileRendererPropsWithLoading {
  scratchFile: GetAnalysisScratchFile;
  fileType: string;
  loadContent: true;
  children: (content: string) => JSX.Element;
}

interface ExternalFileRendererPropsWithoutLoading {
  scratchFile: GetAnalysisScratchFile;
  fileType: string;
  loadContent: false;
  children: (signedURL: string) => JSX.Element;
}

type ExternalFileRendererProps =
  | ExternalFileRendererPropsWithLoading
  | ExternalFileRendererPropsWithoutLoading;

export default function ExternalFileRenderer({
  scratchFile,
  fileType,
  loadContent,
  children,
}: ExternalFileRendererProps): JSX.Element {
  return (
    <MaybeRenderWithSignedURL
      externalFileId={scratchFile.externalFileId}
      url={scratchFile.downloadUrl!}
      renderWithSignedURL={(signedURL) => {
        if (signedURL === undefined) {
          return <CenteredHorizontalThrobber />;
        } else if (signedURL == null) {
          return (
            <InlineBanner bannerType={InlineBannerType.ERROR} text={`Error loading ${fileType} file.`} />
          );
        } else {
          if (loadContent) {
            return (
              <ExternalFileContentLoader signedURL={signedURL} fileType={fileType}>
                {children}
              </ExternalFileContentLoader>
            );
          } else {
            return children(signedURL);
          }
        }
      }}
      owningEntityApiId={scratchFile.id}
    />
  );
}
